import React, { useEffect, useState, useContext } from 'react';
import { AdminContext } from '../AdminContext';

export default function Users() {
  const { token } = useContext(AdminContext);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    role: 'volunteer'
  });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [createSuccess, setCreateSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [deletingUserId, setDeletingUserId] = useState(null);
  const [deleteError, setDeleteError] = useState('');

  // Filter users based on search term
  const filteredUsers = users.filter(user =>
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.role?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Sort users
  const sortedUsers = [...filteredUsers].sort((a, b) => {
    let aVal = a[sortConfig.key] || '';
    let bVal = b[sortConfig.key] || '';
    
    // Handle dates
    if (sortConfig.key === 'created_at') {
      aVal = new Date(aVal);
      bVal = new Date(bVal);
    }
    
    if (sortConfig.direction === 'asc') {
      return aVal > bVal ? 1 : -1;
    } else {
      return aVal < bVal ? 1 : -1;
    }
  });

  // Pagination
  const totalPages = Math.ceil(sortedUsers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedUsers = sortedUsers.slice(startIndex, startIndex + itemsPerPage);

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
    setCurrentPage(1); // Reset to first page when sorting
  };

  const getSortIcon = (column) => {
    if (sortConfig.key !== column) return '↕️';
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/.netlify/functions/admin-list-users', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      let data;
      try {
        data = await res.json();
      } catch (jsonErr) {
        // Handle non-JSON responses (like HTML error pages)
        const text = await res.text();
        if (text.startsWith('<!DOCTYPE') || text.startsWith('<html')) {
          throw new Error('Server returned HTML instead of JSON. Please check if the backend is running correctly.');
        }
        throw new Error('Invalid JSON response from server');
      }
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch users');
      }
      setUsers(data.users || []);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setCreating(true);
    setCreateError('');
    setCreateSuccess('');
    
    try {
      const res = await fetch('/.netlify/functions/admin-create-user', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newUser)
      });
      
      let data;
      try {
        data = await res.json();
      } catch (jsonErr) {
        // Handle non-JSON responses (like HTML error pages)
        const text = await res.text();
        if (text.startsWith('<!DOCTYPE') || text.startsWith('<html')) {
          throw new Error('Server returned HTML instead of JSON. Please check if the backend is running correctly.');
        }
        throw new Error('Invalid JSON response from server');
      }
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to create user');
      }
      
      setCreateSuccess('User created successfully!');
      setNewUser({ email: '', password: '', role: 'volunteer' });
      fetchUsers(); // Refresh users list
      
      // Close modal after successful creation
      setTimeout(() => {
        setShowAddUser(false);
        setCreateSuccess('');
      }, 2000);
      
    } catch (err) {
      console.error('Error creating user:', err);
      setCreateError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }

    setDeletingUserId(userId);
    setDeleteError('');

    try {
      const res = await fetch(`/.netlify/functions/admin-delete-user/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      let data;
      try {
        data = await res.json();
      } catch (jsonErr) {
        // Handle non-JSON responses (like HTML error pages)
        const text = await res.text();
        if (text.startsWith('<!DOCTYPE') || text.startsWith('<html')) {
          throw new Error('Server returned HTML instead of JSON. Please check if the backend is running correctly.');
        }
        throw new Error('Invalid JSON response from server');
      }

      if (!data.success) {
        throw new Error(data.error || 'Failed to delete user');
      }

      // Refresh users list
      fetchUsers();
    } catch (err) {
      console.error('Error deleting user:', err);
      setDeleteError(err.message);
    } finally {
      setDeletingUserId(null);
    }
  };

  const handleCloseModal = () => {
    setShowAddUser(false);
    setNewUser({ email: '', password: '', role: 'volunteer' });
    setCreateError('');
    setCreateSuccess('');
  };

  return (
    <div style={{ maxWidth: '100%', margin: '40px auto', padding: 24 }}>
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2>All Users</h2>
          <button
            onClick={() => setShowAddUser(true)}
            style={{
              padding: '10px 20px',
              background: '#006400',
              color: 'white',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            Add New User
          </button>
        </div>
        
        {/* Search and Stats */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 20,
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1); // Reset to first page when searching
              }}
              style={{
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: 6,
                width: 250,
                fontSize: '14px'
              }}
            />
            <span style={{ color: '#666', fontSize: '14px' }}>
              Showing {paginatedUsers.length} of {filteredUsers.length} users
            </span>
          </div>
        </div>
      </div>
      
      {loading && <div style={{ textAlign: 'center', padding: '20px' }}>Loading...</div>}
      {error && <div style={{ color: 'red', padding: '10px', backgroundColor: '#ffebee', borderRadius: 4, marginBottom: 20 }}>{error}</div>}
      {deleteError && <div style={{ color: 'red', padding: '10px', backgroundColor: '#ffebee', borderRadius: 4, marginBottom: 20 }}>{deleteError}</div>}
      
      {!loading && !error && (
        <div style={{ background: 'white', borderRadius: 8, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          {paginatedUsers.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              {filteredUsers.length === 0 && searchTerm
                ? `No users found matching "${searchTerm}"`
                : 'No users found.'
              }
            </div>
          ) : (
            <>
              {/* Enhanced Table */}
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead style={{ backgroundColor: '#f8f9fa' }}>
                    <tr>
                      <th
                        onClick={() => handleSort('email')}
                        style={{
                          padding: '12px 16px',
                          textAlign: 'left',
                          borderBottom: '2px solid #e0e0e0',
                          cursor: 'pointer',
                          userSelect: 'none',
                          fontWeight: 600,
                          color: '#333'
                        }}
                      >
                        Email {getSortIcon('email')}
                      </th>
                      <th
                        onClick={() => handleSort('role')}
                        style={{
                          padding: '12px 16px',
                          textAlign: 'left',
                          borderBottom: '2px solid #e0e0e0',
                          cursor: 'pointer',
                          userSelect: 'none',
                          fontWeight: 600,
                          color: '#333'
                        }}
                      >
                        Role {getSortIcon('role')}
                      </th>
                      <th
                        onClick={() => handleSort('created_at')}
                        style={{
                          padding: '12px 16px',
                          textAlign: 'left',
                          borderBottom: '2px solid #e0e0e0',
                          cursor: 'pointer',
                          userSelect: 'none',
                          fontWeight: 600,
                          color: '#333'
                        }}
                      >
                        Created {getSortIcon('created_at')}
                      </th>
                      <th style={{
                        padding: '12px 16px',
                        textAlign: 'center',
                        borderBottom: '2px solid #e0e0e0',
                        fontWeight: 600,
                        color: '#333'
                      }}>
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedUsers.map((user, index) => (
                      <tr
                        key={user.id}
                        style={{
                          borderBottom: '1px solid #f0f0f0',
                          backgroundColor: index % 2 === 0 ? '#fafafa' : 'white'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#e3f2fd'}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = index % 2 === 0 ? '#fafafa' : 'white';
                        }}
                      >
                        <td style={{ padding: '12px 16px', color: '#006400', fontWeight: 500 }}>
                          {user.email}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <span style={{
                            padding: '4px 8px',
                            borderRadius: 12,
                            backgroundColor: user.role === 'admin' ? '#ffebee' : user.role === 'reviewer' ? '#e3f2fd' : '#f3e5f5',
                            color: user.role === 'admin' ? '#c62828' : user.role === 'reviewer' ? '#1565c0' : '#7b1fa2',
                            fontSize: '12px',
                            fontWeight: 600
                          }}>
                            {user.role}
                          </span>
                        </td>
                        <td style={{ padding: '12px 16px', color: '#666', fontSize: '14px' }}>
                          {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                          <button
                            onClick={() => handleDeleteUser(user.id)}
                            disabled={deletingUserId === user.id}
                            style={{
                              padding: '6px 12px',
                              background: deletingUserId === user.id ? '#ccc' : '#dc3545',
                              color: 'white',
                              border: 'none',
                              borderRadius: 4,
                              cursor: deletingUserId === user.id ? 'not-allowed' : 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            {deletingUserId === user.id ? 'Deleting...' : 'Delete'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '20px',
                  borderTop: '1px solid #f0f0f0',
                  gap: '8px'
                }}>
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #ddd',
                      background: currentPage === 1 ? '#f5f5f5' : 'white',
                      color: currentPage === 1 ? '#999' : '#333',
                      borderRadius: 4,
                      cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    Previous
                  </button>
                  
                  <span style={{ padding: '0 16px', color: '#666', fontSize: '14px' }}>
                    Page {currentPage} of {totalPages}
                  </span>
                  
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #ddd',
                      background: currentPage === totalPages ? '#f5f5f5' : 'white',
                      color: currentPage === totalPages ? '#999' : '#333',
                      borderRadius: 4,
                      cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Add User Modal */}
      {showAddUser && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: 30,
            borderRadius: 10,
            width: '400px',
            maxWidth: '90%',
            maxHeight: '90%',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3>Add New User</h3>
              <button 
                onClick={handleCloseModal}
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  fontSize: '24px', 
                  cursor: 'pointer',
                  color: '#666'
                }}
              >
                ×
              </button>
            </div>
            
            <form onSubmit={handleCreateUser}>
              <div style={{ marginBottom: 15 }}>
                <label style={{ display: 'block', marginBottom: 5, fontWeight: 'bold' }}>Email:</label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                  required
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ccc',
                    borderRadius: 4,
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              
              <div style={{ marginBottom: 15 }}>
                <label style={{ display: 'block', marginBottom: 5, fontWeight: 'bold' }}>Password:</label>
                <input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                  required
                  minLength={6}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ccc',
                    borderRadius: 4,
                    boxSizing: 'border-box'
                  }}
                />
                <small style={{ color: '#666', fontSize: '12px' }}>Minimum 6 characters</small>
              </div>
              
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', marginBottom: 5, fontWeight: 'bold' }}>Role:</label>
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                  required
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ccc',
                    borderRadius: 4,
                    boxSizing: 'border-box'
                  }}
                >
                  <option value="volunteer">Volunteer</option>
                  <option value="reviewer">Reviewer</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              
              {createError && (
                <div style={{ 
                  color: '#d32f2f', 
                  backgroundColor: '#ffebee', 
                  padding: '8px', 
                  borderRadius: 4, 
                  marginBottom: 15 
                }}>
                  {createError}
                </div>
              )}
              
              {createSuccess && (
                <div style={{ 
                  color: '#2e7d32', 
                  backgroundColor: '#e8f5e8', 
                  padding: '8px', 
                  borderRadius: 4, 
                  marginBottom: 15 
                }}>
                  {createSuccess}
                </div>
              )}
              
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button 
                  type="button" 
                  onClick={handleCloseModal}
                  style={{
                    padding: '8px 16px',
                    background: '#f5f5f5',
                    border: '1px solid #ddd',
                    borderRadius: 4,
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={creating}
                  style={{
                    padding: '8px 16px',
                    background: creating ? '#ccc' : '#006400',
                    color: 'white',
                    border: 'none',
                    borderRadius: 4,
                    cursor: creating ? 'not-allowed' : 'pointer'
                  }}
                >
                  {creating ? 'Creating...' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
